import {
    RiArrowUpLine, RiArrowUpSLine, RiCalendarScheduleLine, RiEdit2Line, RiFlashlightLine,
} from "@remixicon/react";
import React, {
    createRef, FormEvent, ReactElement, useCallback, useEffect, useState,
} from "react";
import {
    Button, ButtonGroup, Form, Modal,
} from "react-bootstrap";
import { useDropzone } from "react-dropzone";
import { BiPencil } from "react-icons/bi";
import { FaTelegramPlane } from "react-icons/fa";
import { GrAction } from "react-icons/gr";
import { MdScheduleSend } from "react-icons/md";
import { RiCloseFill, RiCloseLine } from "react-icons/ri";
import ReactTimeago from "react-timeago";
import classNames from "classnames";
import {
    ActionExecuteStartCondition,
    ConversationSourceType,
    SendFromType, TicketInput, useCreateUploadTokenMutation, useGetIntegrationsQuery, useStartChatMutation,
} from "../../__generated__/api";
import useKeyboardShortcuts, { useKeyboardState } from "../../contexts/KeyboardShortcutContext";
import usePortal from "../../contexts/PortalContext";
import useRouting, { isCurrentRoute } from "../../contexts/Routing";
import { useTheme } from "../../contexts/Theme";
import ActionExecute from "../action-execute/ActionExecute";
import Editor from "../agent-v3/editor/Editor";
import { UploadedFile } from "../agent/text-entry/text-entry";
import Attachment from "../attachment/Attachment";
import EditorLegacy from "../editor/Editor";
import ConversationSources from "../pickers/conversation-sources/ConversationSources";
import Scheduler from "../scheduler/Scheduler";
import Select, { Option, SelectInstanceHandler } from "../select/Select";
import ThemeButton from "../theme/button/Button";
import TimedToast from "../timed-toast/TimedToast";
import Styles from "./StartConversation.module.scss";
import SchedulerIcon from "../scheduler/SchedulerIcon";
import Dropdown from "../theme/dropdown/Dropdown";

const FEATURE_COMPOSE_ACTIONS = "frontend.compose.actions";
const CONVERSATION_STATE = "create-conversations";
export default function StartConversation(): ReactElement {
    const { Paths, historyPush, createHistoryPush } = useRouting();
    const isComposeRoute = isCurrentRoute(Paths.AGENT_COMPOSE);
    const { featureFlags } = usePortal();

    const [conversationSourceIds, setConversationSourceIds] = useState<string[]>();
    const [text, setText] = useState<string>();
    const [from, setFrom] = useState<SendFromType>(SendFromType.Me);
    const [sendChat] = useStartChatMutation();
    const [showCancelSend, setShowCancelSend] = useState<boolean>(false);
    const [shouldShowScheduler, setShowScheduler] = useState<boolean>(false);
    const [scheduledDate, setScheduledDate] = useState<Date>();
    const [ticketInput, setTicketInput] = useState<TicketInput>();
    const [startedActionId, setStartedActionId] = useState<string>();
    const theme = useTheme();

    const { data: integrations } = useGetIntegrationsQuery({
        skip: !featureFlags.has(FEATURE_COMPOSE_ACTIONS),
        variables: {
            shouldIncludeServices: false,
        },
    });

    const [lastPayload, setLastPayload] = useState<{to: string[], body: string, sendFrom: SendFromType}>();
    const [attachments, setAttachments] = useState<UploadedFile[]>([]);

    const selectRef = createRef<SelectInstanceHandler>();
    const keyboardState = useKeyboardState();

    useEffect(() => {
        selectRef.current?.focus();
        if (isComposeRoute) {
            keyboardState.pushContext(CONVERSATION_STATE);
        } else {
            keyboardState.popContext(CONVERSATION_STATE);
        }
    }, [isComposeRoute]);

    const [uploadAttachment] = useCreateUploadTokenMutation();

    const shouldShow = isComposeRoute && !showCancelSend;
    const shouldShowToast = showCancelSend;

    const onDrop = useCallback(async (acceptedFiles: File[]) => {
        const files = await acceptedFiles.mapConcurrent(async (file: File): Promise<UploadedFile> => {
            const meta = await uploadAttachment({
                variables: {
                    attachment: {
                        filename: file.name,
                        size: file.size,
                        mimeType: file.type,
                    },
                },
            });

            if (!meta.data?.createUploadToken || meta.data.createUploadToken?.error) {
                return {
                    name: file.name,
                    mimeType: file.type,
                    error: meta.data?.createUploadToken?.error ?? "Error uploading file",
                };
            }
            const { url, token } = meta.data.createUploadToken;

            const response = await fetch(url, {
                method: "POST",
                body: file,
                credentials: "include",
            });

            if (response.status !== 201) {
                return {
                    name: file.name,
                    mimeType: file.type,
                    error: "Error uploading file. Server did not return 201",
                };
            }

            const json = await response.json() as {url: string};

            return {
                name: file.name,
                mimeType: file.type,
                url: json.url,
                token,
            };
        });

        setAttachments(current => (current ?? []).concat(files));

        // Do something with the files
    }, []);
    const {
        getRootProps, getInputProps, isDragActive,
    } = useDropzone({ onDrop, noClick: true });

    useKeyboardShortcuts(shortcuts => {
        shortcuts.on({
            shortcut: "c",
            context: "default",
            group: "Conversations",
            description: "Start a new conversation",
            handler: createHistoryPush(Paths.AGENT_COMPOSE),
        });
        shortcuts.on({
            shortcut: "esc",
            context: CONVERSATION_STATE,
            handler: createHistoryPush(Paths.AGENT),
        });
    });

    const sendConversation = (data: string | undefined, event?: FormEvent<HTMLFormElement>) => {
        event?.preventDefault();
        if (!data) {
            return;
        }
        const element = event?.target as HTMLFormElement;
        if (element && !element.checkValidity()) {
            element.reportValidity();
            return;
        }
        setLastPayload({
            body: text as string,
            sendFrom: from as SendFromType,
            to: conversationSourceIds as string[],
        });
        setText("");
        setShowCancelSend(true);
        setConversationSourceIds([]);
    };
    const cancelSend = () => {
        if (!lastPayload) {
            return;
        }
        setShowCancelSend(() => false);
        const { body, sendFrom, to } = lastPayload;
        setText(body);
        setConversationSourceIds(to);
        setFrom(sendFrom);
    };

    const sendPayload = () => {
        if (!lastPayload) {
            return;
        }
        const { body, sendFrom, to } = lastPayload;
        setLastPayload(undefined);
        setShowCancelSend(() => false);
        setAttachments([]);
        setScheduledDate(undefined);
        setTicketInput(undefined);
        const attachmentTokens = attachments.toIdList("token").filterNull();
        (async () => {
            await sendChat({
                variables: {
                    message: {
                        body,
                        sendFrom,
                        to,
                        attachmentTokens,
                        action: ticketInput,
                        scheduledDate,
                    },
                },
            });
        })();

        historyPush(Paths.AGENT);
    };

    const forms = integrations?.forms.filter(
        row => row.actionSettings?.startCondition === ActionExecuteStartCondition.BeforeOrAfterStarted,
    );

    return (
        <>
            <TimedToast
                title="Sending message..."
                shouldShow={shouldShowToast}
                seconds={3}
                onClose={sendPayload}
                canCancel
                onCancel={cancelSend}
            >
                <div>
                    Sending message...
                </div>
            </TimedToast>

            <Modal
                show={shouldShow}
                size="lg"
                onHide={createHistoryPush(Paths.AGENT)}
                fullscreen="lg-down"
                dialogClassName={`${Styles.container} card bg-white shadow border-0`}
                contentClassName="border-0 h-100"
                enforceFocus={false}
            >
                {startedActionId && (
                    <ActionExecute
                        actionId={startedActionId}
                        buttonText="Schedule"
                        ticketInput={ticketInput}
                        onHide={() => setStartedActionId(undefined)}
                        onExecuted={input => {
                            setStartedActionId(undefined);
                            setTicketInput(input);
                        }}
                    >
                        <div className="mb-3">
                            <h4>Schedule Action</h4>
                            <div className="text-light">
                                Would you like to schedule an action to execute when the message is sent?
                                The following action will be executed on the created ticket. If there are
                                multiple receipients, the following action will execute once per recepient.
                            </div>
                        </div>
                    </ActionExecute>
                )}

                <form onSubmit={event => sendConversation(text, event)} className="h-100 w-100 m-0 p-0 d-flex flex-column">

                    <Modal.Body className="h-100 w-100 m-0 p-0 d-flex flex-column">
                        {theme.isV3 ? (
                            <Modal.Header className="d-flex">
                                <h4>Compose</h4>
                                <ThemeButton
                                    isClose
                                    variant="none"
                                    size="small"
                                    onClick={createHistoryPush(Paths.AGENT)}
                                />
                            </Modal.Header>
                        ) : (
                            <div className="card-header bg-gradient text-white d-flex justify-content-between align-items-center">
                                <div>
                                    Compose
                                </div>
                                <button
                                    aria-label="Close modal"
                                    type="button"
                                    className="btn btn-none m-0 p-1 px-2"
                                    onClick={createHistoryPush(Paths.AGENT)}
                                >
                                    <RiCloseLine className="text-light" />
                                </button>
                            </div>
                        )}

                        <div className="flex-grow-1 p-3 d-flex flex-column" {...getRootProps()}>

                            {isDragActive && (
                                <div
                                    className="fs-4 text-light d-flex justify-content-center align-items-center flex-grow-1"
                                >
                                    Drag and Drop files here
                                </div>
                            )}

                            {/* eslint-disable-next-line react/jsx-props-no-spreading */}
                            <input {...getInputProps()} />
                            <div className={isDragActive ? "d-none" : "d-flex flex-column h-100"}>

                                <Form.Text>
                                    Who should this message be sent to?
                                </Form.Text>
                                <ConversationSources
                                    ref={selectRef}
                                    placeholder="To"
                                    onSelected={rows => setConversationSourceIds(rows)}
                                    isMulti
                                    sourceTypes={[ConversationSourceType.MicrosoftTeams, ConversationSourceType.SlackChannel]}
                                    minRequired={1}
                                    value={conversationSourceIds}
                                />

                                <Form.Text className="mt-3">
                                    Who should the message be sent from?
                                </Form.Text>

                                <Select
                                    minRequired={1}
                                    placeholder="From"
                                    onChange={(value: Option<unknown>) => setFrom(value.value as SendFromType)}
                                    value={{
                                        label: from === SendFromType.Me ? "Me" : "Agent",
                                        value: from,
                                    }}
                                    options={[
                                        {
                                            label: "Me",
                                            value: SendFromType.Me,
                                        },
                                        {
                                            label: "Agent",
                                            value: SendFromType.Agent,
                                        },
                                    ]}
                                />
                                {!theme.isV3 && (
                                    <Form.Text className="mt-4">
                                        What is the message
                                    </Form.Text>
                                )}
                                {theme.isV3 ? (
                                    <Editor
                                        onSend={() => sendConversation(text)}
                                        className={Styles["text-container"]}
                                        onChange={setText}
                                        mentionSourceId={conversationSourceIds?.firstElement}
                                        value={text ?? ""}
                                    />
                                ) : (
                                    <EditorLegacy
                                        onCommandEnterPressed={data => sendConversation(data)}
                                        className={`${Styles["text-container"]} p-0 pt-2 pb-2 px-2 mt-1 m-0 h-100 flex-grow-1`}
                                        onChange={setText}
                                        mentionSourceId={conversationSourceIds?.firstElement}
                                        text={lastPayload ? lastPayload.body : undefined}
                                    />
                                )}

                                {attachments?.isNotEmpty && (
                                    <div className="d-flex align-items-stretch flex-wrap">
                                        {attachments.map(attach => (
                                            <Attachment
                                                size="sm"
                                                className="mt-1 me-2"
                                                url={attach.url as string}
                                                mimeType={attach.mimeType}
                                                title={attach.name}
                                                onClose={() => setAttachments(attachments.filter(row => row.url !== attach.url))}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>

                        </div>

                    </Modal.Body>
                    <Modal.Footer className="justify-content-between">
                        <div>

                            {ticketInput ? (
                                <div
                                    className={theme.isV3 ? Styles["ticket-action"] : classNames(
                                        "text-primary w-100 border border-blue-dark text-blue-dark px-3 py-2",
                                        "rounded d-flex align-items-center",
                                        Styles["ticket-action-legacy"],
                                    )}
                                >
                                    <span className={Styles["ticket-action-name"]}>
                                        {theme.isV3 ? <RiFlashlightLine /> : <FaTelegramPlane />}
                                        {" "}
                                        {forms?.find(form => form.id === ticketInput.formId)?.name}
                                    </span>
                                    {(ticketInput.customFields?.isNotEmpty
                                        || ticketInput.tags
                                        || ticketInput.subject
                                        || ticketInput.description
                                        || ticketInput.priority
                                        || ticketInput.requestorId)
                                        && (
                                            <ThemeButton
                                                aria-label="Edit Action"
                                                variant="none"
                                                size="xsmall"
                                                className="ms-2"
                                                onClick={() => setStartedActionId(ticketInput.formId)}
                                                prefixIcon={<RiEdit2Line className={theme.isV3 ? "" : "text-blue-dark"} />}
                                            />
                                        )}
                                    <ThemeButton
                                        type="button"
                                        aria-label="Cancel Action"
                                        variant="none"
                                        size="xsmall"
                                        className="ms-2"
                                        onClick={() => setTicketInput(undefined)}
                                        prefixIcon={<RiCloseFill className={theme.isV3 ? "" : "text-blue-dark"} />}
                                    />

                                </div>

                            ) : (
                                forms?.isNotEmpty && (
                                    <Dropdown drop="up">
                                        <Dropdown.Toggle
                                            variant={theme.isV3 ? "secondary" : "none"}
                                            className={theme.isV3 ? "" : "menu-small btn-none p-1"}
                                            size="small"
                                            prefixIcon={theme.isV3 ? <RiFlashlightLine /> : <GrAction />}
                                        />
                                        <Dropdown.Menu>
                                            <Dropdown.Header className="text-wrap">
                                                Action
                                            </Dropdown.Header>
                                            {forms.sortBy("name").map(action => (
                                                <Dropdown.Item
                                                    key={action.id}
                                                    eventKey={action.id}
                                                    className={theme.isV3 ? "" : Styles["action-item"]}
                                                    onClick={() => setStartedActionId(action.id)}
                                                    prefixIcon={theme.isV3 ? <RiFlashlightLine /> : <FaTelegramPlane />}
                                                >
                                                    {action.name}
                                                </Dropdown.Item>
                                            ))}
                                        </Dropdown.Menu>
                                    </Dropdown>
                                )
                            )}
                        </div>

                        <div className="d-flex align-items-center">
                            {scheduledDate && (
                                theme.isV3 ? (
                                    <SchedulerIcon scheduledDate={scheduledDate} onClear={() => setScheduledDate(undefined)} />
                                ) : (
                                    <div
                                        className="w-100 border border-blue-dark text-blue-dark px-3 py-2
                                            rounded border d-flex align-items-center me-3"
                                    >
                                        <span>
                                            <MdScheduleSend className="text-blue-dark" />
                                            {" "}
                                            <ReactTimeago
                                                date={scheduledDate}
                                                title={`Sending at ${scheduledDate.toLocaleString()}`}
                                            />
                                        </span>
                                        <button
                                            aria-label="Clear Schedule"
                                            type="button"
                                            className="btn btn-none p-0 ms-2"
                                            onClick={() => setScheduledDate(undefined)}
                                        >
                                            <RiCloseFill className="text-blue-dark" />
                                        </button>

                                    </div>
                                )
                            )}
                            <div className="d-flex justify-content-end">
                                <Button
                                    variant={theme.isV3 ? "outline-gray" : "secondary"}
                                    className="me-2"
                                    onClick={createHistoryPush(Paths.AGENT)}
                                >
                                    Close
                                </Button>

                                <Scheduler
                                    date={scheduledDate}
                                    shouldShow={shouldShowScheduler}
                                    onCancel={() => setShowScheduler(false)}
                                    title="Schedule Message"
                                    onSelected={date => {
                                        setShowScheduler(false);
                                        setScheduledDate(date);
                                    }}
                                />
                                <Dropdown as={ButtonGroup} drop="up" className="align-self-end">

                                    <Button
                                        type="submit"
                                        variant="primary"
                                        data-test="delete-btn"
                                    >
                                        Send
                                    </Button>
                                    <Dropdown.Toggle
                                        split="left"
                                        variant="primary"
                                        className="px-2 py-2 rounded-end border-start"
                                        prefixIcon={theme.isV3 ? <RiArrowUpSLine /> : undefined}
                                    />

                                    <Dropdown.Menu flip>
                                        <Dropdown.Item
                                            prefixIcon={<RiCalendarScheduleLine />}
                                            onClick={() => setShowScheduler(true)}
                                            className={Styles["action-item"]}
                                        >
                                            Schedule
                                        </Dropdown.Item>
                                    </Dropdown.Menu>
                                </Dropdown>
                            </div>

                        </div>
                    </Modal.Footer>
                </form>

            </Modal>

        </>
    );
}
